<template>
  <div class="bdc_main_wrap whero">
    <div v-if="showEmptyDashboardMessage" class="thw_content">
      No Available CrossView Dashboards to show.
    </div>
    <div v-else class="">
      <div class="thw_content">
        <div class="internal-sub-nav">
          <div
            v-for="(tab, i) in filteredTabOptions"
            :key="i"
            class="isn-item"
            :class="{ active: tab.id === selectedTab, disabled: tab.disabled }"
            @click="onTabClick(tab)"
          >
            {{ tab.value }}
          </div>
        </div>
      </div>
      <div v-if="tableauJwtToken" class="bdc_top_panel_wrap">
        <tableau-viz
          id="tableauViz"
          ref="tableauContainer"
          :src="tableauViewURL"
          :token="tableauJwtToken"
          :on-viz-load-error="handleTableauError"
          toolbar="top"
          hide-tabs
        >
          <custom-parameter name=":showShareOptions" value="false"></custom-parameter>
          <custom-parameter name=":customViews" value="false"></custom-parameter>

          <viz-filter field="Advertiser" :value="advertiserDspIds"> </viz-filter>

          <viz-filter field=":refresh" value="yes"></viz-filter>
          <viz-parameter field="embed" value="yes"></viz-parameter>
        </tableau-viz>
      </div>
      <div v-else-if="!tableauJwtToken && !loading">
        Something went wrong. Please contact Digital Remedy technical support.
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'underscore';
import { get } from 'vuex-pathify';
import { TableauEventType } from '@tableau/embedding-api';
import {
  OVERVIEW_TAB_OPTIONS,
  DELIVERY_TAB_OPTIONS,
  PERFORMANCE_TAB_OPTIONS,
} from '@/cross-view-constant';
import {
  FEATURE_CROSS_VIEW_AUDIENCE_DEMOGRAPHICS,
  FEATURE_CROSS_VIEW_NETWORKS,
  FEATURE_CROSS_VIEW_PERFORMANCE,
  KEY_DEMO_ADVERTISER_ID,
  DEMO_CROSS_VIEW_DEFAULT_ADVERTISER_ID,
  DEMO_CROSS_VIEW_DEFAULT_XANDR_ID,
} from '@/constant';
import EventBus from '@/adready-vue/helpers/global/event-bus';
import { isDemoInstance } from '~/util/utility-functions';
import config from '~/config';
import tableauMixin from '~/components/mixins/tableau-mixin';
import appConsoleDataLayer from '~/api/app-console-data';
import advertiserApi from '~/api/advertiser';

export default {
  mixins: [tableauMixin],
  props: {
    activeDashboard: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      tableauJwtToken: null,
      tableauError: false,
      viz: null,
      loading: false,
      dspAdvertiserIds: [],
      selectedTabId: null,
      showEmptyDashboardMessage: false,
      demoAdvertiserId: null,
      demoUniversalPixelId: null,
      advertiserDspIds: '',
    };
  },
  computed: {
    advertiser: get('common/advertiser'),
    actualUniversalPixelId: get('common/universalPixelId'),
    demoAdvertisers: get('common/GET_DEMO_ADVERTISER_MAPPING_LIST'),
    tableauViewURL() {
      return `${config.TABLEAU_URL}${this.selectedTabUrl}`;
    },
    tabOptions() {
      if (this.activeDashboard?.title === 'Delivery & Engagement') {
        return DELIVERY_TAB_OPTIONS;
      }
      if (this.activeDashboard?.title === 'Performance') {
        return PERFORMANCE_TAB_OPTIONS;
      }
      return OVERVIEW_TAB_OPTIONS;
    },
    defaultSelectedTab() {
      if (this.filteredTabOptions.length === 0) {
        return null;
      }
      return this.filteredTabOptions.find((t) => t.selected) || this.filteredTabOptions[0];
    },
    selectedTab() {
      return this.activeTab?.id || null;
    },
    selectedTabTitle() {
      return this.activeTab?.value || null;
    },
    selectedTabUrl() {
      if (isDemoInstance()) {
        return this.activeTab?.demoViewUrl || null;
      }
      return this.activeTab?.viewUrl || null;
    },
    activeTab() {
      const tabById = this.filteredTabOptions.find((t) => t.id === this.selectedTabId);
      if (tabById) {
        return tabById;
      }
      return this.defaultSelectedTab;
    },
    advertiserHasFeatureCrossViewAudienceDemographics() {
      return this.advertiser?.features?.includes(FEATURE_CROSS_VIEW_AUDIENCE_DEMOGRAPHICS);
    },
    advertiserHasFeatureCrossViewNetworks() {
      return this.advertiser?.features?.includes(FEATURE_CROSS_VIEW_NETWORKS);
    },
    advertiserHasFeatureCrossViewPerformance() {
      return this.advertiser?.features?.includes(FEATURE_CROSS_VIEW_PERFORMANCE);
    },
    filteredTabOptions() {
      const hiddenTabs = [];
      if (!this.advertiserHasFeatureCrossViewAudienceDemographics) {
        hiddenTabs.push('audience-demographics');
      }
      if (!this.advertiserHasFeatureCrossViewNetworks) {
        hiddenTabs.push('network');
      }
      if (!this.advertiserHasFeatureCrossViewPerformance) {
        hiddenTabs.push('linear-performance');
      }
      const filteredTabOptions = this.tabOptions.filter((tab) => !hiddenTabs.includes(tab.id));
      return filteredTabOptions;
    },
  },
  asyncComputed: {
    universalPixelId: {
      default: null,
      async get() {
        if (isDemoInstance()) {
          const demoUniversalPixelId = await this.getDemoUniversalPixelId(this.advertiser);
          return demoUniversalPixelId;
        }
        return this.actualUniversalPixelId;
      },
    },
  },
  watch: {
    tableauJwtToken: {
      deep: true,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          const tableauComponent = document.getElementById('tableauViz');
          if (!o && !this.viz) {
            await this.initializeTableau(tableauComponent);
          }
          if (this.viz) {
            this.viz.addEventListener(TableauEventType.VizLoadError, this.handleTableauError);
          }
        }
      },
    },
    activeDashboard: {
      deep: true,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          // Prevent reverting to Performance tab after refresh and removing Performance feature
          if (
            this.activeDashboard?.title === 'Performance' &&
            this.activeTab === null &&
            this.filteredTabOptions?.length === 0 &&
            o?.title === 'Overview'
          ) {
            this.activeDashboard = o;
          }
        }
      },
    },
    dspAdvertiserIds: {
      deep: true,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (isDemoInstance()) {
            this.advertiserDspIds = n || DEMO_CROSS_VIEW_DEFAULT_XANDR_ID;
          }
          this.advertiserDspIds = n;
          await this.initTableau(false);
        }
      },
    },
  },
  async mounted() {
    if (
      this.activeDashboard?.title === 'Performance' &&
      this.activeTab === null &&
      this.filteredTabOptions?.length === 0
    ) {
      this.$nextTick(() => {
        localStorage.removeItem('selectedPath');
        this.showEmptyDashboardMessage = true;
      });
    }
    this.loading = true;
    await this.fetchDsp();
    await this.initTableau(false);
  },
  methods: {
    onTabClick(tab) {
      if (tab.disabled || this.filteredTabOptions?.length === 1) {
        return;
      }
      if (
        this.activeDashboard?.title === 'Performance' &&
        this.activeTab === null &&
        this.filteredTabOptions?.length === 0
      ) {
        this.$nextTick(() => {
          localStorage.removeItem('selectedPath');
          this.showEmptyDashboardMessage = true;
        });
      }
      this.selectedTabId = tab.id;

      this.$store.set('dashboard/selectedDashboardTab', tab.id);

      this.debounceTableauAuthToken();
    },
    async fetchDsp(retries = 0) {
      const maxRetries = 20;
      if (retries > maxRetries) {
        console.error('Max retries reached for fetchDsp');
        return;
      }
      if (this.advertiser?.id) {
        try {
          const advertiserId = isDemoInstance()
            ? await this.getDemoAdvertiserId()
            : this.advertiser.id;
          const response = await advertiserApi.getCrossviewDspIds({ advertiserId });
          if (response && response.length) {
            this.dspAdvertiserIds = response.filter((res) => res).join('\\,');
          } else {
            this.dspAdvertiserIds = '';
          }
        } catch (error) {
          console.error('Error fetching CrossView DSP IDs: ', error);
          this.dspAdvertiserIds = '';
        }
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      await this.fetchDsp(retries + 1);
    },
    async getDemoAdvertiserId(retries = 20) {
      // Wait for Vuex store to populate demoAdvertisers
      // If the object is empty or null, we shouldn't calculate yet.
      if (!this.demoAdvertisers || Object.keys(this.demoAdvertisers).length === 0) {
        if (retries <= 0) {
          console.warn('Max retries reached: demoAdvertisers store is empty. Using Default.');
          return DEMO_CROSS_VIEW_DEFAULT_ADVERTISER_ID;
        } // Wait 100ms and try again

        await new Promise((resolve) => setTimeout(resolve, 100));
        return this.getDemoAdvertiserId(retries - 1);
      }

      const demoAdvertiserId = parseInt(localStorage.getItem(KEY_DEMO_ADVERTISER_ID), 10); // Check if the ID from local storage exists in the loaded keys
      const demoAdvertiserMappingArr = this.demoAdvertisers.filter(
        (k) => parseInt(k.advertiserId, 10) === demoAdvertiserId
      );

      let demoAdvertiserMapping = null;

      if (Array.isArray(demoAdvertiserMappingArr) && demoAdvertiserMappingArr.length > 0) {
        // Look for solutionId === 7 (Cross View)
        demoAdvertiserMapping = demoAdvertiserMappingArr.find((m) => m.solutionId === 7);
      }

      return demoAdvertiserMapping?.targetAdvertiserId || DEMO_CROSS_VIEW_DEFAULT_ADVERTISER_ID;
    },
    async getDemoUniversalPixelId(retries = 20) {
      let universalPixelId = null;

      if (retries < 0) {
        console.error('Max retries reached for getDemoUniversalPixelId');
        return null;
      }

      const advertiserId = await this.getDemoAdvertiserId();

      await appConsoleDataLayer.flipPixels(advertiserId).then((pixels) => {
        if (pixels && _.isArray(pixels)) {
          universalPixelId = pixels.find((p) => p.conversion_type === 'Universal');
          universalPixelId = universalPixelId ? universalPixelId.flip_id : null;

          if (universalPixelId) {
            EventBus.universalPixelIdReady = true;
          }
        }
      });

      if (universalPixelId) {
        return universalPixelId;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.getDemoUniversalPixelId(retries - 1);
    },
  },
};
</script>
<style lang="scss" scoped>
.bdc_main_wrap.whero {
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
}
.body-content.fullmax .thw_content {
  max-width: 100%;
}
.thw_content {
  max-width: 100%;
  margin: auto;
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
  padding: 0px;
  position: relative;
  z-index: 11;
}
.sopened .thw_content {
  max-width: 100%;
}
.internal-sub-nav {
  display: inline-block;
  float: none;
  margin: 11px 20px 31px 5px;
  display: block;
}
.isn-item {
  display: inline-block;
  margin-right: 20px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primarydark2);
  position: relative;
  cursor: pointer;
}

.isn-item.active {
  color: var(--primarydark);
}

.isn-item.active::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarycolor);
}

.isn-item:hover {
  color: var(--primarydark);
}

.isn-item:hover::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarydark2);
}
@media (max-width: 750px) {
  .bdc_top_panel_wrap {
    flex-wrap: wrap !important;
  }
}
.bdc_top_panel_wrap {
  display: flex;
  justify-content: space-between;
  position: relative;
  top: 3;
}
.chart-sec {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
